/**
 * Categoria e tipo de um veículo — e a lista de quem ficou sem categoria.
 *
 * **A categoria não é enfeite de cadastro: é metade da regra de acesso.** Quando
 * um cliente abre um conteúdo, `ContratoDB.PermiteAcesso` exige uma linha em
 * `Licenca_CategoriaVeiculo` casando a licença dele com a categoria *daquele
 * veículo*. A cláusula é um `EXISTS` — sem linha ele é falso, e **veículo sem
 * categoria não é aberto por contrato nenhum**.
 *
 * Em 14/09/2026 são **104 veículos sem categoria**, e **30 deles já têm conteúdo
 * publicado**: 195 linhas de busca e 11 conteúdos que nenhum cliente consegue
 * abrir. O item aparece na pesquisa e a abertura falha calada.
 *
 * **O tipo** (Hatch, Sedã, SUV, Utilitário) não entra em regra de acesso nenhuma
 * — é classificação de catálogo, e só 27 veículos têm. Vem junto porque é o
 * mesmo gesto na mesma ficha, mas sem alarme: avisar sobre o que não machuca é
 * como se ensina alguém a ignorar aviso.
 *
 * **Gravar é o conjunto inteiro, não um por vez.** O sistema antigo inclui e
 * exclui um vínculo por clique; a API recebe a lista completa. Assim nunca fica
 * meia alteração aplicada se a outra metade falhar.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { api, foraDoProjeto, obter } from "./cliente"
import { catalogoPorProjeto } from "./query"

export type OpcaoDoVeiculo = {
  id: number
  nome: string
  /** Este veículo tem esta categoria/tipo. */
  tem: boolean
}

type Escopo = "categorias" | "tipos"

function usarAlcance(veiculoId: number, escopo: Escopo) {
  return useQuery({
    queryKey: ["veiculo-alcance", escopo, veiculoId] as const,
    enabled: veiculoId > 0,
    queryFn: () => obter<OpcaoDoVeiculo[]>(`api/veiculos/${veiculoId}/${escopo}`, {}),
  })
}

export function usarCategoriasDoVeiculo(veiculoId: number) {
  return usarAlcance(veiculoId, "categorias")
}

export function usarTiposDoVeiculo(veiculoId: number) {
  return usarAlcance(veiculoId, "tipos")
}

/**
 * Grava o conjunto. `foraDoProjeto` porque isto é dado global — a trava recusa
 * em desenvolvimento, e é o certo: não existe veículo canário.
 */
export function usarSalvarAlcance(veiculoId: number, escopo: Escopo) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (ids: number[]) => {
      await api.put(`api/veiculos/${veiculoId}/${escopo}`, { ids }, foraDoProjeto())
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["veiculo-alcance", escopo, veiculoId] })
      // A contagem do topo da lista muda quando um veículo deixa de estar sem
      // categoria — é o número que a pessoa acabou de vir consertar.
      if (escopo === "categorias") qc.invalidateQueries({ queryKey: ["veiculos-sem-categoria"] })
    },
  })
}

// ------------------------------------------------------------------ os órfãos

export type VeiculoSemCategoria = {
  id: number
  marca: string
  modelo: string
  /** Linhas de `TabelaPesquisa` — o que o cliente vê na busca e não abre. */
  linhas: number
  conteudos: number
}

export type VeiculosSemCategoria = {
  total: number
  /** Destes, quantos já têm conteúdo publicado — os que doem. */
  comConteudo: number
  linhas: number
  /** Conteúdos distintos no acervo inteiro, não a soma dos de cada veículo. */
  conteudos: number
  veiculos: VeiculoSemCategoria[]
}

/**
 * Contar não resolve; achar resolve. São 1.202 veículos — procurar os 104 a um a
 * um não é procura. A lista vem inteira e ordenada pelo estrago.
 */
export function usarVeiculosSemCategoria() {
  return useQuery({
    queryKey: ["veiculos-sem-categoria"] as const,
    ...catalogoPorProjeto,
    queryFn: () => obter<VeiculosSemCategoria>("api/veiculos/sem-categoria", {}),
  })
}
